import { useState } from 'react';

import {
  formatDeviation,
  formatViolationActual,
  formatViolationContextLabel,
  formatWpsLimitRange,
  type WpsViolationRecord,
} from '../../../utils/wpsViolationModel';

interface WpsViolationLocationTableProps {
  records: WpsViolationRecord[];
  collapsed?: boolean;
  maxVisible?: number;
}

export const WpsViolationLocationTable = ({
  records,
  collapsed = false,
  maxVisible = 3,
}: WpsViolationLocationTableProps) => {
  const [expanded, setExpanded] = useState(false);

  if (!records.length) return null;

  const shouldCollapse = collapsed && records.length > maxVisible;
  const visible = shouldCollapse && !expanded ? records.slice(0, maxVisible) : records;
  const hiddenCount = shouldCollapse && !expanded ? records.length - maxVisible : 0;

  return (
    <div className="mt-2 overflow-x-auto rounded-lg border border-[#E2E5EA]">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-page-bg text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
          <tr>
            <th className="px-2 py-1.5">Level</th>
            <th className="px-2 py-1.5">Location</th>
            <th className="px-2 py-1.5">Actual</th>
            <th className="px-2 py-1.5">WPS limit</th>
            <th className="px-2 py-1.5">Deviation</th>
            <th className="px-2 py-1.5">Welder ID</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((record, idx) => (
            <tr key={`${record.level}-${record.passName ?? ''}-${idx}`} className="border-t border-[#EEF0F3]">
              <td className="px-2 py-1.5 capitalize text-text-grey">{record.level}</td>
              <td className="px-2 py-1.5 text-blueprint-blue">
                {formatViolationContextLabel({
                  level: record.level,
                  passName: record.passName,
                  zoneName: record.zoneName,
                  tiltStart: record.tiltStart,
                  tiltEnd: record.tiltEnd,
                })}
              </td>
              <td className="px-2 py-1.5 font-medium tabular-nums text-blueprint-blue">
                {formatViolationActual(record.actual, record.parameterKey)}
              </td>
              <td className="px-2 py-1.5 tabular-nums text-text-grey">
                {formatWpsLimitRange(record.min, record.max)}
              </td>
              <td className="px-2 py-1.5 font-medium text-readable-red">
                {formatDeviation(record)}
              </td>
              <td className="px-2 py-1.5 text-text-grey">{record.welderId ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full border-t border-[#EEF0F3] px-2 py-1.5 text-left text-[11px] font-medium text-blueprint-blue hover:bg-page-bg"
        >
          +{hiddenCount} more location{hiddenCount === 1 ? '' : 's'}
        </button>
      )}
      {shouldCollapse && expanded && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="w-full border-t border-[#EEF0F3] px-2 py-1.5 text-left text-[11px] font-medium text-blueprint-blue hover:bg-page-bg"
        >
          Show less
        </button>
      )}
    </div>
  );
};
